/**
 * Dashboard View Component
 *
 * Displays:
 * - Summary cards (My Requests, Pending Approvals, Approved)
 * - Recent activity feed
 * - Recent requests list
 * - Quick action buttons
 */

import { renderIcon } from '../utils/icons';
import { createLink } from '../components/link';
import { createSidebarLayout } from '../components/sidebar-layout';
import { apiClient } from '../services/api-client';
import {
  subscribeToAuth,
  selectUser,
  selectIsAuthenticated,
  type AuthState,
} from '../services/auth-store';

/**
 * Expense report stats by status
 */
interface ExpenseReportStats {
  draft: { count: number; totalChf: number };
  submitted: { count: number; totalChf: number };
  paid: { count: number; totalChf: number };
}

/**
 * Recent expense report summary
 */
interface RecentExpenseReport {
  id: string;
  requestTitle: string;
  totalAmountChf: number | null;
  status: string;
  createdAt: string;
  receiptCount: number;
}

/**
 * Dashboard summary data from API
 */
interface DashboardSummary {
  myRequests: {
    total: number;
    draft: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  pendingApprovals: number;
  recentActivity: RecentActivity[];
  recentRequests: RecentRequest[];
  myExpenseReports: ExpenseReportStats;
  recentExpenseReports: RecentExpenseReport[];
}

/**
 * Recent activity item
 */
interface RecentActivity {
  id: string;
  action: string;
  description: string;
  timestamp: string;
  requestId?: string;
  requestTitle?: string;
}

/**
 * Recent request summary
 */
interface RecentRequest {
  id: string;
  title: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
}

/**
 * Status badge colors
 */
const statusColors: Record<string, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-muted', text: 'text-muted-foreground' },
  PENDING: { bg: 'bg-warning/20', text: 'text-warning' },
  APPROVED: { bg: 'bg-success/20', text: 'text-success' },
  REJECTED: { bg: 'bg-destructive/20', text: 'text-destructive' },
  CANCELLED: { bg: 'bg-muted', text: 'text-muted-foreground' },
  SUBMITTED: { bg: 'bg-info/20', text: 'text-info' },
  PAID: { bg: 'bg-success/20', text: 'text-success' },
};

/**
 * Format currency amount
 */
function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format relative time
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CH', { month: 'short', day: 'numeric' });
}

/**
 * Create a summary card element
 */
function createSummaryCard(
  icon: string,
  iconColor: string,
  label: string,
  value: number | string,
  subtext?: string,
  href?: string
): HTMLElement {
  const card = document.createElement('div');
  const baseClasses = 'glass-sm p-4 rounded-xl transition-all hover:scale-[1.02] hover:shadow-lg';
  const clickableClasses = href ? ' cursor-pointer' : '';
  card.className = baseClasses + clickableClasses;

  card.innerHTML = `
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-lg bg-${iconColor}/20 flex items-center justify-center shrink-0">
        ${renderIcon(icon as Parameters<typeof renderIcon>[0], { size: 'md', className: `text-${iconColor}` }).outerHTML}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-muted-foreground truncate">${label}</p>
        <p class="text-2xl font-bold text-foreground">${value}</p>
        ${subtext ? `<p class="text-xs text-muted-foreground mt-0.5">${subtext}</p>` : ''}
      </div>
    </div>
  `;

  // Add click handler for SPA navigation
  if (href) {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', href);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }

  return card;
}

/**
 * Create an expense report card element (shows count + CHF total)
 */
function createExpenseReportCard(
  icon: string,
  iconColor: string,
  label: string,
  count: number,
  totalChf: number,
  href?: string
): HTMLElement {
  const card = document.createElement('div');
  const baseClasses = 'glass-sm p-4 rounded-xl transition-all hover:scale-[1.02] hover:shadow-lg';
  const clickableClasses = href ? ' cursor-pointer' : '';
  card.className = baseClasses + clickableClasses;

  const formattedChf = new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(totalChf);

  card.innerHTML = `
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-lg bg-${iconColor}/20 flex items-center justify-center shrink-0">
        ${renderIcon(icon as Parameters<typeof renderIcon>[0], { size: 'md', className: `text-${iconColor}` }).outerHTML}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-muted-foreground truncate">${label}</p>
        <p class="text-2xl font-bold text-foreground">${count}</p>
        <p class="text-xs text-muted-foreground mt-0.5">${formattedChf}</p>
      </div>
    </div>
  `;

  // Add click handler for SPA navigation
  if (href) {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', href);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }

  return card;
}

/**
 * Create an activity item element
 */
function createActivityItem(activity: RecentActivity): HTMLElement {
  const item = document.createElement('div');
  item.className = 'flex items-start gap-3 py-3 border-b border-border/50 last:border-0';

  const iconMap: Record<string, { icon: string; color: string }> = {
    REQUEST_CREATED: { icon: 'plus', color: 'primary' },
    REQUEST_UPDATED: { icon: 'edit', color: 'info' },
    REQUEST_SUBMITTED: { icon: 'send', color: 'primary' },
    REQUEST_CANCELLED: { icon: 'xCircle', color: 'destructive' },
    APPROVAL_GRANTED: { icon: 'checkCircle', color: 'success' },
    APPROVAL_REJECTED: { icon: 'xCircle', color: 'destructive' },
    COMMENT_ADDED: { icon: 'fileText', color: 'muted-foreground' },
    ATTACHMENT_ADDED: { icon: 'paperclip', color: 'muted-foreground' },
  };

  const { icon, color } = iconMap[activity.action] || { icon: 'info', color: 'muted-foreground' };

  item.innerHTML = `
    <div class="w-8 h-8 rounded-full bg-${color}/20 flex items-center justify-center shrink-0">
      ${renderIcon(icon as Parameters<typeof renderIcon>[0], { size: 'sm', className: `text-${color}` }).outerHTML}
    </div>
    <div class="flex-1 min-w-0">
      <p class="text-sm text-foreground">${escapeHtml(activity.description)}</p>
      ${activity.requestTitle ? `<p class="text-xs text-muted-foreground truncate">${escapeHtml(activity.requestTitle)}</p>` : ''}
      <p class="text-xs text-muted-foreground mt-1">${formatRelativeTime(activity.timestamp)}</p>
    </div>
  `;

  return item;
}

/**
 * Create a request list item element
 */
function createRequestItem(request: RecentRequest): HTMLElement {
  const item = document.createElement('a');
  item.href = `/requests/${request.id}`;
  item.className =
    'flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer';

  const defaultStyle = { bg: 'bg-muted', text: 'text-muted-foreground' };
  const statusStyle = statusColors[request.status] ?? defaultStyle;
  const bg = statusStyle.bg;
  const text = statusStyle.text;

  item.innerHTML = `
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium text-foreground truncate">${escapeHtml(request.title)}</p>
      <p class="text-xs text-muted-foreground">${formatRelativeTime(request.createdAt)}</p>
    </div>
    <div class="text-right shrink-0">
      <p class="text-sm font-medium text-foreground">${formatAmount(request.amount, request.currency)}</p>
      <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${text}">
        ${request.status.charAt(0) + request.status.slice(1).toLowerCase()}
      </span>
    </div>
  `;

  // Handle click navigation
  item.addEventListener('click', (e) => {
    e.preventDefault();
    window.history.pushState({}, '', `/requests/${request.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  });

  return item;
}

/**
 * Create an expense report list item element
 */
function createReportItem(report: RecentExpenseReport): HTMLElement {
  const item = document.createElement('a');
  item.href = `/expense-reports/${report.id}`;
  item.className =
    'flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer';

  const defaultStyle = { bg: 'bg-muted', text: 'text-muted-foreground' };
  const statusStyle = statusColors[report.status] ?? defaultStyle;
  const bg = statusStyle.bg;
  const text = statusStyle.text;

  const formattedAmount = report.totalAmountChf !== null
    ? new Intl.NumberFormat('en-CH', {
        style: 'currency',
        currency: 'CHF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(report.totalAmountChf)
    : 'CHF 0.00';

  item.innerHTML = `
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium text-foreground truncate">${escapeHtml(report.requestTitle)}</p>
      <p class="text-xs text-muted-foreground">${formatRelativeTime(report.createdAt)} · ${report.receiptCount} receipt${report.receiptCount !== 1 ? 's' : ''}</p>
    </div>
    <div class="text-right shrink-0">
      <p class="text-sm font-medium text-foreground">${formattedAmount}</p>
      <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${text}">
        ${report.status.charAt(0) + report.status.slice(1).toLowerCase().replace(/_/g, ' ')}
      </span>
    </div>
  `;

  // Handle click navigation
  item.addEventListener('click', (e) => {
    e.preventDefault();
    window.history.pushState({}, '', `/expense-reports/${report.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  });

  return item;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Create the dashboard view
 */
export function createDashboardView(): HTMLElement {
  // Create main content
  const content = document.createElement('div');
  content.className = 'container mx-auto px-4 lg:px-6 py-6';

  // Welcome section
  const welcomeSection = document.createElement('div');
  welcomeSection.className = 'mb-8';
  welcomeSection.innerHTML = `
    <p class="text-muted-foreground" id="welcome-text">Welcome back! Here's an overview of your requests and reports.</p>
  `;

  // Subscribe to update welcome message with user name
  const unsubscribe = subscribeToAuth((state: AuthState) => {
    const user = selectUser(state);
    const isAuthenticated = selectIsAuthenticated(state);
    if (isAuthenticated && user) {
      const welcomeText = document.getElementById('welcome-text');
      if (welcomeText) {
        welcomeText.textContent = `Welcome back, ${user.givenName || user.displayName}! Here's an overview of your requests and reports.`;
      }
    }
  });

  // Store unsubscribe for cleanup
  (content as HTMLElement & { cleanup?: () => void }).cleanup = unsubscribe;

  // Quick actions
  const quickActions = document.createElement('div');
  quickActions.className = 'flex flex-wrap gap-4 mb-8';

  const newRequestBtn = createLink({
    href: '/requests/new',
    content: `
      <span class="flex items-center gap-2">
        ${renderIcon('plus', { size: 'sm' }).outerHTML}
        <span>New Expense Request</span>
      </span>
    `,
    className:
      'inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/25',
  });

  quickActions.appendChild(newRequestBtn);

  // Expenditure Requests Section Header
  const requestsHeader = document.createElement('h2');
  requestsHeader.className = 'text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3';
  requestsHeader.textContent = 'Expenditure Requests';

  // Expenditure Requests cards grid
  const requestsGrid = document.createElement('div');
  requestsGrid.className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8';
  requestsGrid.id = 'requests-grid';

  // Loading placeholders for requests
  for (let i = 0; i < 4; i++) {
    const placeholder = document.createElement('div');
    placeholder.className = 'glass-sm p-4 rounded-xl animate-pulse';
    placeholder.innerHTML = `
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-lg bg-muted"></div>
        <div class="flex-1">
          <div class="h-4 bg-muted rounded w-24 mb-2"></div>
          <div class="h-6 bg-muted rounded w-12"></div>
        </div>
      </div>
    `;
    requestsGrid.appendChild(placeholder);
  }

  // Expense Reports Section Header
  const expenseReportsHeader = document.createElement('h2');
  expenseReportsHeader.className = 'text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3';
  expenseReportsHeader.textContent = 'Expense Reports';

  // Expense Reports cards grid
  const expenseReportsGrid = document.createElement('div');
  expenseReportsGrid.className = 'grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8';
  expenseReportsGrid.id = 'expense-reports-grid';

  // Loading placeholders for expense reports
  for (let i = 0; i < 3; i++) {
    const placeholder = document.createElement('div');
    placeholder.className = 'glass-sm p-4 rounded-xl animate-pulse';
    placeholder.innerHTML = `
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-lg bg-muted"></div>
        <div class="flex-1">
          <div class="h-4 bg-muted rounded w-24 mb-2"></div>
          <div class="h-6 bg-muted rounded w-12"></div>
          <div class="h-3 bg-muted rounded w-16 mt-1"></div>
        </div>
      </div>
    `;
    expenseReportsGrid.appendChild(placeholder);
  }

  // Content grid (recent activity + recent requests + recent reports)
  const contentGrid = document.createElement('div');
  contentGrid.className = 'grid grid-cols-1 lg:grid-cols-3 gap-8';

  // Recent activity card
  const activityCard = document.createElement('div');
  activityCard.className = 'glass-card p-6 rounded-2xl';
  activityCard.innerHTML = `
    <div class="flex items-center gap-3 mb-4">
      ${renderIcon('clock', { size: 'md', className: 'text-primary' }).outerHTML}
      <h2 class="text-lg font-semibold text-foreground">Recent Activity</h2>
    </div>
    <div id="activity-list" class="space-y-1">
      <div class="animate-pulse py-3">
        <div class="h-4 bg-muted rounded w-3/4 mb-2"></div>
        <div class="h-3 bg-muted rounded w-1/2"></div>
      </div>
      <div class="animate-pulse py-3">
        <div class="h-4 bg-muted rounded w-2/3 mb-2"></div>
        <div class="h-3 bg-muted rounded w-1/3"></div>
      </div>
    </div>
  `;

  // Recent requests card
  const recentRequestsCard = document.createElement('div');
  recentRequestsCard.className = 'glass-card p-6 rounded-2xl';
  recentRequestsCard.innerHTML = `
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        ${renderIcon('fileText', { size: 'md', className: 'text-primary' }).outerHTML}
        <h2 class="text-lg font-semibold text-foreground">Recent Requests</h2>
      </div>
      <a href="/my-requests" class="text-sm text-primary hover:text-primary/80 transition-colors">View all</a>
    </div>
    <div id="requests-list" class="space-y-1">
      <div class="animate-pulse p-3">
        <div class="h-4 bg-muted rounded w-3/4 mb-2"></div>
        <div class="h-3 bg-muted rounded w-1/2"></div>
      </div>
      <div class="animate-pulse p-3">
        <div class="h-4 bg-muted rounded w-2/3 mb-2"></div>
        <div class="h-3 bg-muted rounded w-1/3"></div>
      </div>
    </div>
  `;

  // Recent reports card
  const recentReportsCard = document.createElement('div');
  recentReportsCard.className = 'glass-card p-6 rounded-2xl';
  recentReportsCard.innerHTML = `
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        ${renderIcon('receipt', { size: 'md', className: 'text-primary' }).outerHTML}
        <h2 class="text-lg font-semibold text-foreground">Recent Reports</h2>
      </div>
      <a href="/expense-reports" class="text-sm text-primary hover:text-primary/80 transition-colors">View all</a>
    </div>
    <div id="reports-list" class="space-y-1">
      <div class="animate-pulse p-3">
        <div class="h-4 bg-muted rounded w-3/4 mb-2"></div>
        <div class="h-3 bg-muted rounded w-1/2"></div>
      </div>
      <div class="animate-pulse p-3">
        <div class="h-4 bg-muted rounded w-2/3 mb-2"></div>
        <div class="h-3 bg-muted rounded w-1/3"></div>
      </div>
    </div>
  `;

  contentGrid.appendChild(activityCard);
  contentGrid.appendChild(recentRequestsCard);
  contentGrid.appendChild(recentReportsCard);

  // Assemble main content
  content.appendChild(welcomeSection);
  content.appendChild(quickActions);
  content.appendChild(requestsHeader);
  content.appendChild(requestsGrid);
  content.appendChild(expenseReportsHeader);
  content.appendChild(expenseReportsGrid);
  content.appendChild(contentGrid);

  // Load dashboard data
  loadDashboardData();

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/dashboard',
    title: 'Dashboard',
    subtitle: 'Overview of your requests and reports',
  });
}

/**
 * Load dashboard data from API
 */
async function loadDashboardData(): Promise<void> {
  try {
    const response = await apiClient.get<{ data: DashboardSummary }>('/dashboard/summary');
    const summary = response.data.data;

    // Update expenditure requests cards
    const requestsGrid = document.getElementById('requests-grid');
    if (requestsGrid) {
      requestsGrid.innerHTML = '';

      const draftCard = createSummaryCard(
        'edit',
        'primary',
        'Draft',
        summary.myRequests.draft,
        undefined,
        '/my-requests?status=DRAFT'
      );

      const pendingCard = createSummaryCard(
        'clock',
        'warning',
        'Pending',
        summary.myRequests.pending,
        undefined,
        '/my-requests?status=PENDING'
      );

      const approvedCard = createSummaryCard(
        'checkCircle',
        'success',
        'Approved',
        summary.myRequests.approved,
        undefined,
        '/my-requests?status=APPROVED'
      );

      const pendingApprovalsCard = createSummaryCard(
        'bell',
        'info',
        'Pending Approvals',
        summary.pendingApprovals,
        'Needs your approval',
        '/approvals'
      );

      requestsGrid.appendChild(draftCard);
      requestsGrid.appendChild(pendingCard);
      requestsGrid.appendChild(approvedCard);
      requestsGrid.appendChild(pendingApprovalsCard);
    }

    // Update expense reports cards
    const expenseReportsGrid = document.getElementById('expense-reports-grid');
    if (expenseReportsGrid) {
      expenseReportsGrid.innerHTML = '';

      const draftReportsCard = createExpenseReportCard(
        'edit',
        'primary',
        'Draft',
        summary.myExpenseReports.draft.count,
        summary.myExpenseReports.draft.totalChf,
        '/expense-reports?status=DRAFT'
      );

      const submittedReportsCard = createExpenseReportCard(
        'send',
        'info',
        'Submitted',
        summary.myExpenseReports.submitted.count,
        summary.myExpenseReports.submitted.totalChf,
        '/expense-reports?status=SUBMITTED'
      );

      const paidReportsCard = createExpenseReportCard(
        'checkCircle',
        'success',
        'Paid',
        summary.myExpenseReports.paid.count,
        summary.myExpenseReports.paid.totalChf,
        '/expense-reports?status=PAID'
      );

      expenseReportsGrid.appendChild(draftReportsCard);
      expenseReportsGrid.appendChild(submittedReportsCard);
      expenseReportsGrid.appendChild(paidReportsCard);
    }

    // Update recent activity
    const activityList = document.getElementById('activity-list');
    if (activityList) {
      activityList.innerHTML = '';
      if (summary.recentActivity.length > 0) {
        summary.recentActivity.forEach((activity) => {
          activityList.appendChild(createActivityItem(activity));
        });
      } else {
        activityList.innerHTML = `
          <div class="py-8 text-center text-muted-foreground">
            <p>No recent activity</p>
          </div>
        `;
      }
    }

    // Update recent requests
    const requestsList = document.getElementById('requests-list');
    if (requestsList) {
      requestsList.innerHTML = '';
      if (summary.recentRequests.length > 0) {
        summary.recentRequests.forEach((request) => {
          requestsList.appendChild(createRequestItem(request));
        });
      } else {
        requestsList.innerHTML = `
          <div class="py-8 text-center text-muted-foreground">
            <p>No requests yet</p>
            <a href="/requests/new" class="text-primary hover:text-primary/80 text-sm mt-2 inline-block">
              Create your first request
            </a>
          </div>
        `;
      }
    }

    // Update recent expense reports
    const reportsList = document.getElementById('reports-list');
    if (reportsList) {
      reportsList.innerHTML = '';
      if (summary.recentExpenseReports.length > 0) {
        summary.recentExpenseReports.forEach((report) => {
          reportsList.appendChild(createReportItem(report));
        });
      } else {
        reportsList.innerHTML = `
          <div class="py-8 text-center text-muted-foreground">
            <p>No expense reports yet</p>
          </div>
        `;
      }
    }
  } catch (error) {
    console.error('Failed to load dashboard data:', error);

    // Show error state
    const requestsGrid = document.getElementById('requests-grid');
    if (requestsGrid) {
      requestsGrid.innerHTML = `
        <div class="col-span-full glass-sm p-6 rounded-xl text-center">
          <p class="text-muted-foreground mb-2">Failed to load dashboard data</p>
          <button onclick="window.location.reload()" class="text-primary hover:text-primary/80 text-sm">
            Refresh page
          </button>
        </div>
      `;
    }
  }
}

/**
 * Dashboard view function for router
 */
export function dashboardView(): HTMLElement {
  return createDashboardView();
}

export default dashboardView;
